import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, Home } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useCart } from '../contexts/CartContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Success = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState('checking');
  const [paymentDetails, setPaymentDetails] = useState(null);
  const { clearCart } = useCart();

  useEffect(() => {
    const checkPaymentStatus = async () => {
      if (!sessionId) {
        setStatus('error');
        return;
      }

      let attempts = 0;
      const maxAttempts = 5;
      const pollInterval = 2000;

      const poll = async () => {
        if (attempts >= maxAttempts) {
          setStatus('timeout');
          return;
        }

        try {
          const response = await axios.get(`${API}/checkout/status/${sessionId}`);
          setPaymentDetails(response.data);

          if (response.data.payment_status === 'paid') {
            setStatus('success');
            clearCart();
            return;
          } else if (response.data.status === 'expired') {
            setStatus('error');
            return;
          }

          attempts++;
          setTimeout(poll, pollInterval);
        } catch (error) {
          console.error('Error verificant el pagament:', error);
          setStatus('error');
        }
      };

      poll();
    };

    checkPaymentStatus();
  }, [sessionId, clearCart]);

  if (status === 'checking') {
    return (
      <div data-testid="checking-payment" className="min-h-screen pt-32 pb-24 px-6 md:px-12 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary mx-auto mb-6"></div>
          <p className="text-muted-foreground">Verificant el pagament...</p>
        </div>
      </div>
    );
  }

  if (status === 'error' || status === 'timeout') {
    return (
      <div data-testid="payment-error" className="min-h-screen pt-32 pb-24 px-6 md:px-12 flex items-center justify-center">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 bg-destructive/10 text-destructive rounded-full flex items-center justify-center mx-auto mb-6">
            <span className="text-3xl">×</span>
          </div>
          <h2 className="font-heading text-3xl font-medium text-foreground mb-4">
            Error en el pagament
          </h2>
          <p className="text-muted-foreground mb-8">
            {status === 'timeout'
              ? 'No hem pogut verificar el pagament. Si us plau, contacta amb nosaltres.'
              : 'Hi ha hagut un problema amb el pagament. Si us plau, torna-ho a intentar.'}
          </p>
          <Link to="/catalog">
            <Button size="lg" className="rounded-full">
              Tornar a la botiga
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="success-page" className="min-h-screen pt-32 pb-24 px-6 md:px-12 flex items-center justify-center">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
        className="text-center max-w-md"
      >
        <div className="w-20 h-20 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-6">
          <CheckCircle className="h-12 w-12" />
        </div>

        <h1 className="font-heading text-4xl md:text-5xl font-medium text-foreground mb-4">
          Pagament correcte!
        </h1>

        <p className="text-lg text-muted-foreground mb-8">
          Gràcies per la teva compra. Rebràs un correu electrònic amb els detalls de la comanda.
        </p>

        {paymentDetails && (
          <div className="bg-muted rounded-2xl p-6 mb-8 text-left">
            <h3 className="font-medium text-foreground mb-4">Detalls del pagament</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Import:</span>
                <span className="font-medium">
                  {paymentDetails.amount_total 
                    ? new Intl.NumberFormat('ca-ES', { 
                        style: 'currency', 
                        currency: paymentDetails.currency || 'EUR' 
                      }).format(paymentDetails.amount_total / 100)
                    : '0,00 €'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Estat:</span>
                <span className="font-medium text-green-600">Pagat</span>
              </div>
            </div>
          </div>
        )}

        <div className="flex flex-col sm:flex-row gap-4">
          <Link to="/" className="flex-1">
            <Button size="lg" className="w-full rounded-full">
              <Home className="mr-2 h-5 w-5" />
              Tornar a l'inici
            </Button>
          </Link>
          <Link to="/catalog" className="flex-1">
            <Button variant="outline" size="lg" className="w-full rounded-full">
              Continuar comprant
            </Button>
          </Link>
        </div>
      </motion.div>
    </div>
  );
};

export default Success;